import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Square, RotateCw, PauseCircle, Trash2, MoreHorizontal, ChevronDown, ChevronRight } from 'lucide-react';
import {
  Button,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useInstanceAction,
  useOpennebula,
  type Instance,
  type InstanceAction,
} from '@/api/opennebula';

const LABELS: Record<InstanceAction, string> = {
  resume: 'Start',
  poweroff: 'Power off',
  reboot: 'Reboot',
  suspend: 'Suspend',
  terminate: 'Terminate',
  poweron: 'Start',
  resize: 'Resize',
};

/**
 * Lifecycle action controls for an instance, shared by the detail header.
 * Dispatches `/perform` and flags the context `isWaitingUpdate` so detail
 * queries switch to adaptive polling until the transition settles.
 *
 * Buttons use the `loading` prop to surface in-flight state inline instead of
 * only disabling, so users get positive feedback that the action was received.
 * An optimistic transition label ("Stopping…" / "Starting…" / "Rebooting…") is
 * shown on the primary button while the mutation is pending.
 */
export function InstanceActionBar({
  serviceId,
  instance,
  onAfterTerminate,
}: {
  serviceId: string;
  instance: Instance;
  onAfterTerminate?: () => void;
}) {
  const { show } = useToast();
  const navigate = useNavigate();
  const { setWaiting } = useOpennebula();
  const [confirmTerminate, setConfirmTerminate] = useState(false);
  const [confirmHalt, setConfirmHalt] = useState<'poweroff' | 'suspend' | null>(null);
  const [confirmReboot, setConfirmReboot] = useState(false);
  // Track which action was last dispatched for optimistic labels.
  const [pendingAction, setPendingAction] = useState<InstanceAction | null>(null);

  const action = useInstanceAction(serviceId, instance.id, {
    onSuccess: (_d, perform) => {
      setWaiting(perform);
      show({ tone: 'success', title: `${LABELS[perform]} requested` });
      if (perform === 'terminate') onAfterTerminate?.();
    },
    onError: () => {
      setPendingAction(null);
      show({ tone: 'danger', title: 'Action failed' });
    },
    onSettled: () => setPendingAction(null),
  });

  const run = (perform: InstanceAction) => {
    setPendingAction(perform);
    action.mutate(perform);
  };

  const status = instance.status;
  const busy = action.isPending;
  const isOn = status === 'POWERED_ON';
  const isTerminated = status === 'TERMINATED';

  if (isTerminated) return null;

  // Optimistic transition label while the mutation is in-flight.
  const transitionLabel = (defaultLabel: string, activeAction: InstanceAction): string => {
    if (!busy || pendingAction !== activeAction) return defaultLabel;
    const map: Partial<Record<InstanceAction, string>> = {
      poweroff: 'Stopping…',
      resume: 'Starting…',
      reboot: 'Rebooting…',
      suspend: 'Suspending…',
    };
    return map[activeAction] ?? defaultLabel;
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
      {isOn ? (
        <>
          <Button
            variant="secondary"
            size="sm"
            startIcon={<Square size={15} />}
            loading={busy && pendingAction === 'poweroff'}
            disabled={busy && pendingAction !== 'poweroff'}
            onClick={() => setConfirmHalt('poweroff')}
          >
            {transitionLabel('Power off', 'poweroff')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            startIcon={<RotateCw size={15} />}
            loading={busy && pendingAction === 'reboot'}
            disabled={busy && pendingAction !== 'reboot'}
            onClick={() => setConfirmReboot(true)}
          >
            {transitionLabel('Reboot', 'reboot')}
          </Button>
        </>
      ) : (
        <Button
          size="sm"
          startIcon={<Play size={15} />}
          loading={busy && pendingAction === 'resume'}
          disabled={busy && pendingAction !== 'resume'}
          onClick={() => run('resume')}
        >
          {transitionLabel('Start', 'resume')}
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton variant="ghost" size="sm" aria-label="More actions" disabled={busy}>
            <MoreHorizontal size={18} />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {isOn && (
            <DropdownMenuItem onSelect={() => setConfirmHalt('suspend')}>
              <PauseCircle size={14} /> Suspend
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => navigate('resize')}>
            <RotateCw size={14} /> Resize
          </DropdownMenuItem>
          <DropdownMenuItem tone="danger" onSelect={() => setConfirmTerminate(true)}>
            <Trash2 size={14} /> Terminate
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={confirmReboot}
        onOpenChange={setConfirmReboot}
        title="Reboot instance?"
        description={`"${instance.name || instance.id}" will restart. Any unsaved work may be lost.`}
        confirmLabel="Reboot"
        loading={busy}
        onConfirm={() => {
          run('reboot');
          setConfirmReboot(false);
        }}
      />

      <ConfirmDialog
        open={confirmTerminate}
        onOpenChange={setConfirmTerminate}
        title="Terminate instance?"
        description={`This permanently destroys "${instance.name || instance.id}" and its disks. This cannot be undone.`}
        confirmLabel="Terminate"
        destructive
        requireText={instance.name || instance.id}
        loading={busy}
        onConfirm={() => {
          run('terminate');
          setConfirmTerminate(false);
        }}
      />

      <HaltConfirmDialog
        action={confirmHalt}
        instance={instance}
        loading={busy}
        onOpenChange={(o) => !o && setConfirmHalt(null)}
        onConfirm={() => {
          if (confirmHalt) run(confirmHalt);
          setConfirmHalt(null);
        }}
      />
    </div>
  );
}

const cellLabel: React.CSSProperties = { fontSize: 'var(--vn-font-size-sm)', fontWeight: 700, color: 'var(--vn-fg-muted)' };
const cellValue: React.CSSProperties = { fontSize: 'var(--vn-font-size-sm)' };

/**
 * Stop/Suspend confirmation. Beyond the basic confirm, surfaces a cost
 * warning (charges continue for associated resources) and an expandable
 * breakdown of the compute, volume and networking resources still billed.
 * Exported so the list-page row dropdown can reuse the same dialog.
 */
export function HaltConfirmDialog({
  action,
  instance,
  loading,
  onOpenChange,
  onConfirm,
}: {
  action: 'poweroff' | 'suspend' | null;
  instance: Instance;
  loading: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const [showResources, setShowResources] = useState(false);
  const verb = action === 'suspend' ? 'Suspend' : 'Power off';
  const res = instance.metadata?.resources;
  const disks = instance.metadata?.disks ?? [];
  const nics = instance.metadata?.nics ?? [];
  const aliasNics = instance.metadata?.nic_alias ?? [];
  const publicNics = nics.filter((n) => /public/i.test(n.network ?? ''));
  const gb = (mb?: string | number) => {
    const n = Number(mb);
    return Number.isFinite(n) && n > 0 ? Math.round((n / 1024) * 10) / 10 : 0;
  };

  return (
    <Dialog open={!!action} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{verb} instance?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)' }}>
              To avoid losing progress, please save any unsaved work before "{instance.name || instance.id}" goes offline.
            </p>
            <div>
              <div style={cellLabel}>Instance ID</div>
              <div style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 600 }}>
                {instance.id}{instance.name ? ` (${instance.name})` : ''}
              </div>
            </div>

            <div
              style={{
                padding: 'var(--vn-space-3)',
                borderRadius: 'var(--vn-radius-md)',
                background: 'var(--vn-warning-subtle)',
                border: '1px solid var(--vn-warning-border)',
              }}
            >
              <div style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 700, color: 'var(--vn-danger-text)' }}>
                You will be charged for any associated resources.
              </div>
              <div style={{ fontSize: 'var(--vn-font-size-sm)', marginTop: 4 }}>
                Stopping or suspending the instance halts charges for its data transfer, but you'll still incur costs for the associated resources.
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowResources((s) => !s)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none',
                  padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 700,
                  fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-accent-text, var(--vn-accent-solid))',
                }}
              >
                {showResources ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                Associated resources
              </button>
              {showResources && (
                <div style={{ display: 'grid', gap: 'var(--vn-space-3)', marginTop: 'var(--vn-space-2)' }}>
                  <div>
                    <div style={cellLabel}>Compute</div>
                    <div style={cellValue}>{res?.cores ?? '?'} vCPU — {res?.memory ?? '?'} GB RAM</div>
                  </div>
                  <div>
                    <div style={cellLabel}>Volumes & Snapshot</div>
                    {disks.length ? disks.map((d) => (
                      <div key={d.disk_id} style={cellValue}>{d.image || `disk-${d.disk_id}`} — {gb(d.size)} GB</div>
                    )) : <div style={cellValue}>—</div>}
                  </div>
                  <div>
                    <div style={cellLabel}>Networking</div>
                    {publicNics.length === 0 && aliasNics.length === 0 ? (
                      <div style={cellValue}>—</div>
                    ) : (
                      <>
                        {publicNics.map((n) => <div key={`p-${n.nic_id}`} style={cellValue}>Public IP {n.ip}</div>)}
                        {aliasNics.map((n) => <div key={`a-${n.nic_id}`} style={cellValue}>Floating IP {n.ip}</div>)}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button loading={loading} onClick={onConfirm}>{verb}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
